import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Admin, CustomRoutes, Resource } from 'react-admin';
import authProvider from './authProvider';
import dataProvider from './dataProvider';
import {
  TenderList,
  TenderEdit,
  TenderCreate,
  TenderShow,
  TenderUpdateShow,
  ArchivedTenderList,
  FailedTenderList,
  ProcessedTenderList,
} from './tenders';
import { UserList, UserEdit, UserCreate } from './users';
import SetPasswordPage from './SetPassword';
import ForgotPassword from './ForgotPassword';
import LoginPage from './LoginPage';
import EmailLandingPage from './EmailLandingPage';
import EmailUnsubscribePage from './EmailUnsubscribePage';
import { MyAccountEdit, MyAccountList } from './MyAccount';
import ChangePassword from './ChangePassword';
import { BookEdit } from './Book';
import i18nProvider from './i18nProvider';
import { NotificationList, NotificationShow } from './notifications';
import { EmailTemplateList, EmailTemplateEdit } from './emailTemplates';
import { BuyerList, BuyerShow, BuyerEdit, BuyerCreate } from './buyers';
import {
  BidderList,
  BidderShow,
  BidderEdit,
  BidderCreate,
  AllBidderList,
  ArchivedBidderList,
  BlockedBidderList,
  DeletedBidderList,
  FailedBidderList,
  SuccessfulBidderList,
  MarketingConsentBidderList,
  LeadyBidderList,
} from './bidders';
import { FailedBidderWorkflowList } from './failedBidderWorkflows';
import { BidderOfferLeadList } from './bidderOfferLeads';
import { BidderOfferArchivedList } from './bidderOfferArchived';
import { BidderCheckDetails } from './components/BidderCheckDetails';
import { EmailList, EmailShow, EmailEdit, EmailCreate } from './emails';
import { OrderPredictionsList } from './orderPredictions';
import ModelStats from './ModelStats';
import TenderProcessingStats from './TenderProcessingStats';
import BidderProcessingStats from './BidderProcessingStats';
import { customTheme } from './theme';
import CustomLayout from './layout/CustomLayout';
import CustomLogin from './components/CustomLogin';
import Dashboard from './Dashboard';
import DashboardV2 from './DashboardV2';

const AdminApp = () => (
  <Admin
    dataProvider={dataProvider}
    authProvider={authProvider}
    loginPage={CustomLogin}
    theme={customTheme}
    i18nProvider={i18nProvider}
    layout={CustomLayout}
    dashboard={Dashboard}
    requireAuth
    disableTelemetry
  >
    <CustomRoutes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/dashboard-v2" element={<DashboardV2 />} />
      <Route path="/predictions/model-stats" element={<ModelStats />} />
      <Route path="/admin/tender-stats" element={<TenderProcessingStats />} />
      <Route path="/admin/bidder-stats" element={<BidderProcessingStats />} />
      <Route
        path="/tenders/:tenderId/updates/:id/show"
        element={<TenderUpdateShow />}
      />
      <Route
        path="/bidders/:bidderId/checks/:checkId/show"
        element={<BidderCheckDetails />}
      />
    </CustomRoutes>
    <Resource
      name="tenders"
      list={TenderList}
      show={TenderShow}
      edit={TenderEdit}
      create={TenderCreate}
      options={{ label: 'Przetargi' }}
    />
    <Resource
      name="tenders/archived"
      list={ArchivedTenderList}
      show={TenderShow}
      options={{ label: 'Archiwalne przetargi' }}
    />
    <Resource
      name="tenders/failed"
      list={FailedTenderList}
      show={TenderShow}
      options={{ label: 'Przetargi z błędami' }}
    />
    <Resource
      name="tenders/processed"
      list={ProcessedTenderList}
      show={TenderShow}
      options={{ label: 'Przetargi przetworzone' }}
    />
    <Resource
      name="notifications"
      list={NotificationList}
      show={NotificationShow}
      options={{ label: 'Powiadomienia' }}
    />
    <Resource
      name="users"
      list={UserList}
      edit={UserEdit}
      create={UserCreate}
    />
    <Resource
      name="email-templates"
      list={EmailTemplateList}
      edit={EmailTemplateEdit}
      options={{ label: 'Szablony Email' }}
    />
    <Resource
      name="email/messages"
      list={EmailList}
      show={EmailShow}
      edit={EmailEdit}
      create={EmailCreate}
      options={{ label: 'Wiadomości Email' }}
    />
    <Resource name="myAccount" list={MyAccountList} edit={MyAccountEdit} />
    <Resource
      name="change-password"
      list={ChangePassword}
      edit={ChangePassword}
    />
    <Resource name="edit-details" list={MyAccountEdit} edit={MyAccountEdit} />
    <Resource
      name="ordering-parties"
      list={BuyerList}
      show={BuyerShow}
      edit={BuyerEdit}
      create={BuyerCreate}
      options={{ label: 'Zamawiający' }}
    />
    <Resource
      name="bidders"
      list={BidderList}
      show={BidderShow}
      edit={BidderEdit}
      create={BidderCreate}
      options={{ label: 'Oferenci' }}
    />
    <Resource
      name="bidders/all"
      list={AllBidderList}
      show={BidderShow}
      edit={BidderEdit}
      create={BidderCreate}
      options={{ label: 'Wszyscy oferenci' }}
    />
    <Resource
      name="bidders/archived"
      list={BidderOfferArchivedList}
      options={{ label: 'Oferenci zarchiwizowani' }}
    />
    <Resource
      name="bidders/deleted"
      list={DeletedBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Usunięci oferenci' }}
    />
    <Resource
      name="bidders/blocked"
      list={BlockedBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Zablokowani oferenci' }}
    />
    <Resource
      name="bidders/failed"
      list={FailedBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Oferenci z błędami' }}
    />
    <Resource
      name="bidders/successful"
      list={SuccessfulBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Oferenci zwycięzcy' }}
    />
    <Resource
      name="bidders/marketing-consent"
      list={MarketingConsentBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Oferenci z sukcesem' }}
    />
    <Resource
      name="bidders/leady"
      list={LeadyBidderList}
      show={BidderShow}
      edit={BidderEdit}
      options={{ label: 'Oferenci Leady' }}
    />
    <Resource
      name="predictions/orders"
      list={OrderPredictionsList}
      options={{ label: 'Zamówienia' }}
    />
    <Resource
      name="workflow/failed-bidder-workflows"
      list={FailedBidderWorkflowList}
      options={{ label: 'Nieudane workflow oferentów' }}
    />
    <Resource
      name="bidders/leads"
      list={BidderOfferLeadList}
      options={{ label: 'Oferenci Leady' }}
    />
    <Resource name="bidder-checks" options={{ label: 'Weryfikacje' }} />
  </Admin>
);

const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/public/email/:token" element={<EmailLandingPage />} />
      <Route path="/public/unsubscribe/:token" element={<EmailUnsubscribePage />} />
      <Route path="/*" element={<AdminApp />} />
    </Routes>
  </BrowserRouter>
);

export default App;
